// Desk booking: a walk-in seated now with a free stylist, or a booking for later with a stylist or
// any available stylist. Times come from the live schedule, so nothing can be double booked.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import api, { addDays, formatDate, isoDate } from '../services/api';
import { Modal, Spinner } from './ui';

const NOW_STATE = {
  free: { label: 'Free now', tone: 'ok' },
  busy: { label: 'Busy', tone: 'danger' },
  off: { label: 'Marked off', tone: 'muted' },
  none: { label: 'Not on the schedule now', tone: 'warn' },
};

const digits = (v) => String(v || '').replace(/\D/g, '');

function niceDay(iso) {
  const today = isoDate();
  if (iso === today) return 'Today';
  if (iso === addDays(today, 1)) return 'Tomorrow';
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' });
}

/**
 * preset: { mode: 'walk_in' | 'later', date, staffId, slotId, any, time }
 */
export default function BookingModal({ preset = {}, onClose, onBooked }) {
  const [mode, setMode] = useState(preset.mode || 'later');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [service, setService] = useState('');
  const [known, setKnown] = useState(null);
  const [services, setServices] = useState([]);
  const [nowData, setNowData] = useState(null);
  const [walkStaff, setWalkStaff] = useState(preset.mode === 'walk_in' ? preset.staffId || null : null);
  const [day, setDay] = useState(preset.date || isoDate());
  const [who, setWho] = useState(preset.any ? 'any' : preset.staffId ? String(preset.staffId) : 'any');
  const [board, setBoard] = useState(null);
  const [pick, setPick] = useState(
    preset.slotId ? { slot_id: preset.slotId, time: preset.time } : preset.any && preset.time ? { starts_at: `${preset.date}T${preset.time}:00`, time: preset.time } : null,
  );
  const [notifyTouched, setNotifyTouched] = useState(false);
  const [notify, setNotify] = useState((preset.mode || 'later') !== 'walk_in');
  const [saving, setSaving] = useState(false);
  const lookupTimer = useRef(null);
  const autoName = useRef(''); // the name filled in from the last lookup, so a new number can replace it

  useEffect(() => {
    api.knownServices().then(setServices).catch(() => {});
  }, []);

  useEffect(() => {
    if (mode !== 'walk_in') return;
    api
      .scheduleNow()
      .then(setNowData)
      .catch((e) => toast.error(e.message));
  }, [mode]);

  useEffect(() => {
    if (mode !== 'later') return;
    setBoard(null);
    api
      .scheduleDay(day)
      .then(setBoard)
      .catch((e) => toast.error(e.message));
  }, [mode, day]);

  useEffect(() => {
    if (!notifyTouched) setNotify(mode !== 'walk_in');
  }, [mode, notifyTouched]);

  // Look the customer up by phone, to fill the name and show their last visit.
  useEffect(() => {
    clearTimeout(lookupTimer.current);
    const d = digits(phone);
    if (d.length < 10) {
      setKnown(null);
      return undefined;
    }
    lookupTimer.current = setTimeout(() => {
      api
        .lookupCustomer(d)
        .then((res) => {
          setKnown(res.found ? res : { found: false });
          const filled = res.found ? res.name || '' : '';
          // Fill the name, or swap it when the number changed; never overwrite what staff typed.
          setName((n) => (!n.trim() || n === autoName.current ? filled : n));
          autoName.current = filled;
        })
        .catch(() => {});
    }, 350);
    return () => clearTimeout(lookupTimer.current);
  }, [phone]);

  const stylists = board?.stylists || [];
  const times = useMemo(() => {
    if (!board) return [];
    return board.rows
      .filter((r) => !r.past)
      .map((r) => {
        if (who === 'any') return r.any.left > 0 ? { time: r.time, label: r.label, starts_at: `${board.date}T${r.time}:00`, left: r.any.left } : null;
        const cell = r.cells[who];
        return cell && cell.state === 'free' ? { time: r.time, label: r.label, slot_id: cell.slot_id } : null;
      })
      .filter(Boolean);
  }, [board, who]);

  // Drop a picked time that is no longer offered (other day or stylist).
  useEffect(() => {
    if (!pick || !board) return;
    const match = times.find((t) => (pick.slot_id ? t.slot_id === pick.slot_id : t.starts_at === pick.starts_at));
    if (!match) setPick(null);
    else if (!pick.label) setPick(match); // a preset from the schedule gets its friendly label
  }, [times, board, pick]);

  const walkChoice = (nowData?.stylists || []).find((s) => s.id === walkStaff);
  const phoneOk = digits(phone).length >= 10;
  const ready =
    phoneOk && service.trim() && (mode === 'walk_in' ? walkChoice && (walkChoice.state === 'free' || walkChoice.state === 'none') : Boolean(pick));

  const whoLabel =
    mode === 'walk_in'
      ? walkChoice?.name || ''
      : who === 'any'
        ? 'any available stylist'
        : stylists.find((s) => String(s.id) === who)?.name || '';
  const whenLabel = mode === 'walk_in' ? 'now' : pick ? `${niceDay(day)}, ${pick.label || pick.time}` : '';

  const submit = async () => {
    if (!ready || saving) return;
    setSaving(true);
    try {
      const payload = { phone: digits(phone), customer_name: name.trim(), service_name: service.trim(), notify };
      if (mode === 'walk_in') Object.assign(payload, { walk_in: true, staff_id: walkStaff });
      else if (pick.slot_id) payload.slot_id = pick.slot_id;
      else payload.starts_at = pick.starts_at;
      const res = await api.createAppointment(payload);
      toast.success(
        mode === 'walk_in'
          ? `${name.trim() || 'Walk-in'} is with ${whoLabel} now.`
          : `Booked: ${name.trim() || 'customer'} · ${service.trim()} · ${whenLabel} with ${whoLabel}.${notify ? ' WhatsApp confirmation sent.' : ''}`,
      );
      onBooked?.(res.appointment);
      onClose();
    } catch (e) {
      toast.error(e.message);
      if (mode === 'later') api.scheduleDay(day).then(setBoard).catch(() => {});
      if (mode === 'walk_in') api.scheduleNow().then(setNowData).catch(() => {});
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="New appointment" onClose={onClose} size="wide">
      <div className="nye-seg" role="tablist" aria-label="Booking type">
        <button type="button" role="tab" aria-selected={mode === 'walk_in'} className={mode === 'walk_in' ? 'active' : ''} onClick={() => setMode('walk_in')}>
          🚶 Walk-in now
        </button>
        <button type="button" role="tab" aria-selected={mode === 'later'} className={mode === 'later' ? 'active' : ''} onClick={() => setMode('later')}>
          📅 Book for later
        </button>
      </div>

      <div className="nye-form-grid nye-form-grid--2 nye-mt-16">
        <label className="nye-field">
          <span className="nye-label">
            Mobile number <span className="nye-req">*</span>
          </span>
          <div className="nye-phone-input">
            <span>+91</span>
            <input
              className="nye-input"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="10-digit mobile"
              autoFocus
            />
          </div>
          {known?.found ? (
            <span className="nye-small nye-ok-text">
              Returning customer{known.name ? `: ${known.name}` : ''}
              {known.last_service ? ` · last: ${known.last_service} on ${formatDate(known.last_visit)}` : ''}
            </span>
          ) : known ? (
            <span className="nye-small nye-muted">New customer</span>
          ) : null}
        </label>
        <label className="nye-field">
          <span className="nye-label">Name on the booking</span>
          <input className="nye-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Who is coming" />
          {known?.found && known.name_confirmed && name.trim() && name.trim().toLowerCase() !== (known.name || '').toLowerCase() ? (
            <span className="nye-small nye-muted">
              Booking for someone else on this number. {known.name} stays the contact; the booking is saved for {name.trim()}.
            </span>
          ) : known?.found && !known.name_confirmed && known.name ? (
            <span className="nye-small nye-muted">From their WhatsApp profile. Check it with them; it is saved as their name.</span>
          ) : null}
        </label>
        <label className="nye-field" style={{ gridColumn: '1 / -1' }}>
          <span className="nye-label">
            Service <span className="nye-req">*</span>
          </span>
          <input className="nye-input" list="nye-booking-services" value={service} onChange={(e) => setService(e.target.value)} placeholder="e.g. Haircut" />
          <datalist id="nye-booking-services">
            {services.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </label>
      </div>

      {mode === 'walk_in' ? (
        <div className="nye-mt-16">
          <div className="nye-section-label" style={{ marginBottom: 8 }}>
            Who will look after them now?
          </div>
          {!nowData ? (
            <Spinner />
          ) : nowData.stylists.length === 0 ? (
            <div className="nye-small nye-muted">No active stylists. Add one in Staff Availability → Stylists.</div>
          ) : (
            <div className="nye-pick-grid">
              {nowData.stylists.map((s) => {
                const cfg = NOW_STATE[s.state] || NOW_STATE.none;
                const usable = s.state === 'free' || s.state === 'none';
                return (
                  <button
                    key={s.id}
                    type="button"
                    disabled={!usable}
                    className={`nye-pick-card${walkStaff === s.id ? ' selected' : ''}`}
                    onClick={() => setWalkStaff(s.id)}
                  >
                    <div className="nye-strong">{s.name}</div>
                    <span className={`nye-pill nye-pill--sm nye-pill--${cfg.tone}`}>
                      {s.state === 'busy' ? `Busy until ${s.until}` : cfg.label}
                    </span>
                    {s.next_free && s.state !== 'free' ? <div className="nye-small nye-muted">Next free {s.next_free}</div> : null}
                  </button>
                );
              })}
            </div>
          )}
          {walkChoice?.state === 'none' ? (
            <div className="nye-callout nye-mt-8">
              {walkChoice.name} has no slot right now. The walk-in is saved anyway and keeps {walkChoice.name} busy for 30 minutes, or until you
              mark the service taken.
            </div>
          ) : null}
        </div>
      ) : (
        <div className="nye-mt-16">
          <div className="nye-row nye-row--wrap" style={{ gap: 8, alignItems: 'center', marginBottom: 10 }}>
            <span className="nye-section-label" style={{ margin: 0 }}>
              Day
            </span>
            {[isoDate(), addDays(isoDate(), 1), addDays(isoDate(), 2)].map((d) => (
              <button key={d} type="button" className={`nye-chip${day === d ? ' active' : ''}`} onClick={() => setDay(d)}>
                {niceDay(d)}
              </button>
            ))}
            <input type="date" className="nye-input nye-input--bordered" style={{ width: 160, padding: '7px 10px' }} value={day} min={isoDate()} onChange={(e) => e.target.value && setDay(e.target.value)} />
          </div>
          <div className="nye-row nye-row--wrap" style={{ gap: 8, alignItems: 'center', marginBottom: 12 }}>
            <span className="nye-section-label" style={{ margin: 0 }}>
              Stylist
            </span>
            <button type="button" className={`nye-chip${who === 'any' ? ' active' : ''}`} onClick={() => setWho('any')}>
              Any available
            </button>
            {stylists.map((s) => (
              <button key={s.id} type="button" className={`nye-chip${who === String(s.id) ? ' active' : ''}`} onClick={() => setWho(String(s.id))}>
                {s.name}
                {!s.working ? ' (not working)' : ''}
              </button>
            ))}
          </div>
          {!board ? (
            <Spinner />
          ) : times.length === 0 ? (
            <div className="nye-callout">
              No free times {who === 'any' ? '' : `for ${whoLabel} `}on {niceDay(day)}. Try another day{who === 'any' ? '' : ' or Any available'}.
            </div>
          ) : (
            <div className="nye-time-grid">
              {times.map((t) => {
                const selected = pick && (t.slot_id ? pick.slot_id === t.slot_id : pick.starts_at === t.starts_at);
                return (
                  <button key={t.time} type="button" className={`nye-time-chip${selected ? ' selected' : ''}`} onClick={() => setPick(t)}>
                    {t.label}
                    {who === 'any' ? <span>{t.left} free</span> : null}
                  </button>
                );
              })}
            </div>
          )}
          {who === 'any' ? (
            <div className="nye-small nye-muted nye-mt-8">
              An any-stylist booking holds one place at that time. Give it a stylist later from the Day schedule or Appointments.
            </div>
          ) : null}
        </div>
      )}

      <label className="nye-check nye-mt-16">
        <input
          type="checkbox"
          checked={notify}
          onChange={(e) => {
            setNotifyTouched(true);
            setNotify(e.target.checked);
          }}
        />
        <span>Send the WhatsApp confirmation and reminders to the customer</span>
      </label>

      <div className="nye-booking-foot">
        <div className="nye-small" style={{ flex: 1, minWidth: 200 }}>
          {ready ? (
            <>
              <strong>{name.trim() || 'Customer'}</strong> · {service.trim()} · {whenLabel} with {whoLabel}
            </>
          ) : (
            <span className="nye-muted">
              {!phoneOk ? 'Enter the mobile number.' : !service.trim() ? 'Enter the service.' : mode === 'walk_in' ? 'Pick a free stylist.' : 'Pick a time.'}
            </span>
          )}
        </div>
        <button type="button" className="nye-btn nye-btn--outline" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="nye-btn nye-btn--primary" disabled={!ready || saving} onClick={submit}>
          {saving ? <Spinner light /> : null}
          {mode === 'walk_in' ? 'Seat walk-in' : 'Book appointment'}
        </button>
      </div>
    </Modal>
  );
}
